import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';
import type { components } from '../api/schema';
import { unwrap } from '../api/unwrap';
import { pruneUndefined } from '../lib/prune';

import type { WarehouseCreateInput, WarehouseStatus, WarehouseUpdateInput } from './schemas';

type CreateWarehouseRequest = components['schemas']['CreateWarehouseRequest'];
type UpdateWarehouseRequest = components['schemas']['UpdateWarehouseRequest'];

export const warehousesKeys = {
  all: ['warehouses'] as const,
  list: (filter: { clientId?: string; includeDeleted: boolean }) =>
    ['warehouses', 'list', filter] as const,
  one: (id: string) => ['warehouses', 'one', id] as const,
};

export interface WarehouseRecord {
  id: string;
  clientId: string;
  name: string;
  description: string | null;
  status: WarehouseStatus;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export const useWarehouses = (options: { clientId?: string; includeDeleted?: boolean } = {}) => {
  const { clientId, includeDeleted = false } = options;
  return useQuery({
    queryKey: warehousesKeys.list({ ...(clientId ? { clientId } : {}), includeDeleted }),
    queryFn: async (): Promise<WarehouseRecord[]> => {
      const query: {
        clientId?: string;
        includeDeleted?: 'true' | 'false';
      } = {};
      if (clientId) query.clientId = clientId;
      if (includeDeleted) query.includeDeleted = 'true';
      const result = await api.GET('/api/warehouses', { params: { query } });
      return unwrap(result).warehouses;
    },
  });
};

export const useWarehouse = (id: string | undefined) =>
  useQuery({
    queryKey: id ? warehousesKeys.one(id) : ['warehouses', 'one', 'pending'],
    enabled: Boolean(id),
    queryFn: async (): Promise<WarehouseRecord> => {
      if (!id) throw new Error('warehouse id required');
      const result = await api.GET('/api/warehouses/{id}', { params: { path: { id } } });
      return unwrap(result).warehouse;
    },
  });

const invalidateWarehouses = (qc: ReturnType<typeof useQueryClient>): Promise<void> =>
  qc.invalidateQueries({ queryKey: warehousesKeys.all });

export const useCreateWarehouse = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: WarehouseCreateInput): Promise<WarehouseRecord> => {
      const payload = pruneUndefined(body) as CreateWarehouseRequest;
      const result = await api.POST('/api/warehouses', { body: payload });
      return unwrap(result).warehouse;
    },
    onSuccess: () => invalidateWarehouses(qc),
  });
};

export const useUpdateWarehouse = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: WarehouseUpdateInput): Promise<WarehouseRecord> => {
      const payload = pruneUndefined(body) as UpdateWarehouseRequest;
      const result = await api.PATCH('/api/warehouses/{id}', {
        params: { path: { id } },
        body: payload,
      });
      return unwrap(result).warehouse;
    },
    onSuccess: () => invalidateWarehouses(qc),
  });
};

export const useDeleteWarehouse = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const result = await api.DELETE('/api/warehouses/{id}', { params: { path: { id } } });
      unwrap(result);
    },
    onSuccess: () => invalidateWarehouses(qc),
  });
};
